import { QueryFactory } from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { Sorting } from '@lmt/unicorn-shared/app/components/common/table/types';
import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CreateGDTMappingDto,
  GDTMappingListDto,
  SortDirection,
} from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { Button } from '../../../common/ui-kit/buttons/Button';
import { MappingsListTable } from './components/MappingsListTable';

import styles from '@lmt/unicorn-shared/app/components/gdt/mappings/list/GDTMappingListPage.module.scss';

export const GDTMappingListPage: React.FC = React.memo(
  function GDTMappingListPage() {
    const i18next = useScopedTranslation('gdt.mapping.list');
    const dispatch = useUniDispatch();
    const modals = useModal();
    const queryCache = useQueryClient();
    const { data: mappings, isFetching } =
      QueryFactory.GDTMappingQuery.useGetAllQuery();
    const changeSorting = useCallback(
      (sorting: Sorting) => {
        const field = sorting.field as keyof GDTMappingListDto;
        if (field) {
          if (sorting.direction === SortDirection.Asc) {
            mappings!.sort((a, b) => (a[field] < b[field] ? 1 : -1));
          } else {
            mappings!.sort((a, b) => (a[field] > b[field] ? 1 : -1));
          }
        }
      },
      [mappings],
    );
    const addConfiguration = useCallback(async () => {
      const result = await modals.showPrompt({
        title: i18next.t('add.title'),
        text: i18next.t('add.text'),
        defaultValue: '',
      });
      if (result) {
        try {
          await ClientFactory.GDTMappingClient.create({
            name: result,
            rules: [],
          });
          await queryCache.invalidateQueries({
            queryKey: QueryFactory.GDTMappingQuery.getAllQueryKey(),
          });
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        }
      }
    }, []);
    return (
      <div className={styles.tableContainer}>
        <div className={styles.patientsTable}>
          <Loading
            loading={isFetching}
            title={i18next.t('table_loading')}
            className={styles.loading}
          >
            <MappingsListTable
              data={mappings ?? []}
              noResults={!!mappings && mappings.length === 0}
              changeSorting={changeSorting}
            />
            <Button
              onClick={addConfiguration}
              title={i18next.t('add_button')}
            />
          </Loading>
        </div>
      </div>
    );
  },
);
